import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { I18N } from 'commons';
import { Field, FieldArray } from 'formik';
import { connect } from 'react-redux';
import DatePickerSection from 'components/forms/DatePickerSection';
import styled from 'styled-components';
import ErrorMessage from '../ErrorMessage';

const DatePickerSectionWrapper = styled.div`
  >div >div:first-child {
    width: 100%;
  }
  .react-datepicker-wrapper {
    &, >div, input {
      width: 100%;
    }
  }
`;

const renderSingleSelectionField = (
  customField,
  values,
  touched,
  errors,
  convertHTMLTags
) => {
  const { attrId, definedValues, editable, mandatory, name } = customField;
  const hasTouched = _.has(touched, attrId);
  const hasError = _.has(errors, attrId) && errors[attrId] !== '';

  return (
    <div>
      <Field
        component={editable ? 'select' : 'Input'}
        readOnly={!editable}
        name={attrId}
        className="form-control notranslate"
        id={attrId}
        aria-labelledby={`${attrId}Label ${(hasTouched && hasError) ? `${attrId}Desc` : ''}`}
        aria-invalid={hasTouched && hasError}
        value={values[attrId]}
        tabIndex="0"
        eb-test={`ssp_additionInfo_select_${attrId}`}
        required={mandatory}
        aria-required={mandatory}
        validate={(value) => {
          let error;
          if (!value && mandatory) {
            error = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
          }
          return error;
        }}
      >
        {
          editable &&
          <>
            <I18N tag="option" value="">ssp.select.null.text</I18N>
            {
              _.map(definedValues, value => (
                <I18N tag="option" value={value} key={value}>
                  {convertHTMLTags(value)}
                </I18N>
              ))
            }
          </>
        }
      </Field>
      {
        (hasTouched && hasError) &&
        <ErrorMessage id={`${attrId}Desc`} aria-hidden="true">
          {errors[attrId]}
        </ErrorMessage>
      }
    </div>
  );
};

const renderTextBoxField = (
  customField,
  values,
  touched,
  errors,
  type,
  convertHTMLTags
) => {
  const { attrId, editable, mandatory, name } = customField;
  const hasTouched = _.has(touched, attrId);
  const hasError = _.has(errors, attrId) && errors[attrId] !== '';
  const reg = /^.{0,11}$/;
  return (
    <div>
      <Field
        autoComplete="off"
        readOnly={!editable}
        type={editable ? type : 'text'}
        name={attrId}
        id={attrId}
        aria-labelledby={`${attrId}Label ${(hasTouched && hasError) ? `${attrId}Desc` : ''}`}
        tabIndex="0"
        className="form-control notranslate"
        value={values[attrId]}
        aria-invalid={hasTouched && hasError}
        eb-test={`ssp_additionInfo_input_${attrId}`}
        required={mandatory}
        aria-required={mandatory}
        validate={(value) => {
          let error;
          if (value) {
            if (type === 'number' && !reg.test(value)) {
              error = 'jquery.validation.number.length';
            } else if (type === 'number' && !/^[-+]?[0-9.]+$/.test(value)) {
              error = 'jquery.validation.number';
            }
          }
          if (!value && mandatory) {
            error = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
          }
          return error;
        }}
      />
      {
        (hasTouched && hasError) &&
        <ErrorMessage id={`${attrId}Desc`} aria-hidden="true">
          {errors[attrId]}
        </ErrorMessage>
      }
    </div>
  );
};

const renderMultipleSelectionField = (
  customField,
  values,
  touched,
  errors,
  convertHTMLTags
) => {
  const { definedValues, editable, mandatory, attrId, name } = customField;
  const processedValues = definedValues.map((item, index) => `${attrId}_${index}`)
  const result1 = [];
  const result2 = [];
  processedValues.forEach((item) => {
    result1.push(_.has(errors, item));
    result2.push(_.has(touched, item));
  });
  const hasError = !result1.includes(false);
  const hasTouched = _.has(touched, attrId) || !result2.includes(false);
  const checkboxError = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
  const multipleValue = values[attrId];

  return (
    <div>
      <FieldArray
        name={attrId}
        render={arrayHelpers => (
          <div role="group" aria-labelledby={`${attrId}Label`} id={attrId}>
            {definedValues.map((singleValue, index) => {
              if (!multipleValue) {
                // workaround for formik bug (fixed in 2.x)
                // when no option is selected at first, the values[attrId] will be an empty string
                // which will cause error from formik
                arrayHelpers.push('')
                arrayHelpers.pop()
              }

              const itemId = `${attrId}_${index}`
              const isChecked = _.includes(multipleValue, singleValue);
              return (
                <div
                  key={`${singleValue}_key`}
                  className="notranslate"
                >
                  <Field
                    id={itemId}
                    type="checkbox"
                    role="checkbox"
                    name={itemId}
                    value={singleValue}
                    disabled={!editable}
                    checked={isChecked}
                    aria-checked={isChecked}
                    aria-invalid={hasTouched && hasError}
                    eb-test={`ssp_additionInfo_checkbox_${attrId}_${singleValue}`}
                    title={singleValue}
                    validate={() => {
                      let error;
                      if (_.isEmpty(multipleValue) && mandatory) {
                        error = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
                      }
                      return error;
                    }}
                    onChange={e => {
                      if (e.target.checked) {
                        arrayHelpers.push(singleValue);
                      } else {
                        const idx = multipleValue.indexOf(singleValue);
                        arrayHelpers.remove(idx);
                      }
                    }}
                  />
                  {singleValue}
                </div>
              );
            })}
          </div>
        )}
      />
      {
        (hasTouched && hasError) &&
        <ErrorMessage id={`${attrId}Desc`} aria-hidden="true">
          {checkboxError}
        </ErrorMessage>
      }
    </div>
  );
};

const renderDateBoxField = (
  customField,
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  convertHTMLTags
) => {
  const { mandatory, editable, attrId, name } = customField;
  const hasTouched = _.has(touched, attrId);
  const hasError = _.has(errors, attrId);
  const value = typeof values[attrId] === 'undefined' ? '' : values[attrId][0];
  const reg = /^[1-9]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])$/;
  return (
    <DatePickerSectionWrapper>
      <Field
        name={attrId}
        validate={([dateValue]) => {
          let error;
          if (dateValue !== undefined && dateValue && !reg.test(dateValue)) {
            error = 'jquery.validation.dateISO';
          }
          if (mandatory && !dateValue) {
            error = `jquery.validation.field.required, ${convertHTMLTags(name)}`;
          }
          return error;
        }}
        render={() => (
          <>
            {
              !editable &&
              <input
                className="sr-only"
                type="text"
                value={value}
                name={attrId}
                aria-labelledby={`${attrId}Label ${(hasTouched && hasError) ? `${attrId}Desc` : ''}`}
                readOnly
                autoComplete="off"
              />
            }
            <DatePickerSection
              id={attrId}
              handleDateChange={dateValue => {
                let resultValue = dateValue === '' ? dateValue : -1;
                if (reg.test(dateValue)) {
                  resultValue = dateValue;
                }
                setFieldValue(attrId, [resultValue]);
                setFieldTouched(attrId, true);
              }}
              invalid={hasTouched && hasError}
              editable={editable}
              selectDate={value}
              attrId={attrId}
              onBlur={() => {
                setFieldTouched(attrId, true);
              }}
              autoComplete="off"
              required={mandatory}
              ariaRequired={mandatory}
            />
          </>
        )}
      />
      {
        (hasTouched && hasError) &&
        <ErrorMessage id={`${attrId}Desc`} aria-hidden="true">
          {errors[attrId]}
        </ErrorMessage>
      }
    </DatePickerSectionWrapper>);
};

const AdditionalInformationRow = ({
  customField,
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  setFieldError,
}) => {
  const { dataType, displayFormat, name, mandatory, attrId } = customField;
  let content;
  let type;
  const dict = {
    // put '&' on top to convert '&' first
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
  }
  const convertHTMLTags = (value) => {
    if (typeof value === 'string') {
      return Object.keys(dict).reduce((prev, curr) => prev.replace(new RegExp(curr, 'g'), dict[curr]), value)
    }
    return value
  }
  if (displayFormat === 'L') {
    content = renderSingleSelectionField(
      customField, values, touched, errors, convertHTMLTags
    );
  } else if (dataType === 'STRING' && displayFormat === 'F') {
    type = 'text';
    content = renderTextBoxField(
      customField, values, touched, errors, type, convertHTMLTags
    );
  } else if (displayFormat === 'S') {
    content = renderMultipleSelectionField(
      customField, values, touched, errors, convertHTMLTags
    );
  } else if (dataType === 'DATE' && displayFormat === 'F') {
    content = renderDateBoxField(
      customField, values, touched, errors, setFieldValue, setFieldTouched, convertHTMLTags
    );
  } else if (dataType === 'NUMBER' && displayFormat === 'F') {
    type = 'number';
    content = renderTextBoxField(
      customField, values, touched, errors, type, convertHTMLTags
    );
  }
  return (
    <div className="form-group row">
      <div className="col-md-12">
        <label className="col-form-label notranslate" htmlFor={attrId} id={`${attrId}Label`}>

          {name}
          {
            mandatory &&
            <span aria-hidden="true" className="required-star">*</span>
          }
          {
            displayFormat == 'S' && mandatory &&
            <span className="sr-only"><I18N>information.label.required</I18N></span>
          }
        </label>
        {content}
      </div>
    </div>
  );
};


AdditionalInformationRow.propTypes = {
  customField: PropTypes.shape().isRequired,
  values: PropTypes.shape().isRequired,
  touched: PropTypes.shape().isRequired,
  errors: PropTypes.shape().isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
};

AdditionalInformationRow.defaultProps = {
};

export default connect()(AdditionalInformationRow);
